import {
  CustomerPortalAccountConfigurationRequest,
  CustomerPortalAccountConfigurationResponse,
  CustomerPortalBillGroupUpdateRequest,
  CustomerPortalBillGroupUpdateResponse,
  CustomerPortalCompanyInfoResponse,
  CustomerPortalConfigurationErrorContext,
  CustomerPortalContactUpdateRequest,
  CustomerPortalContactUpdateResponse,
  CustomerPortalData,
  CustomerPortalInvoice,
  CustomerPortalPaymentMethod,
  CustomerPortalPaymentMethodCreateRequest,
  CustomerPortalPaymentMethodCreateResponse,
  CustomerPortalPaymentMethodTokenRequest,
  CustomerPortalPaymentMethodTokenResponse,
  CustomerPortalPaymentRequest,
  CustomerPortalPaymentResponse,
  CustomerPortalRawData,
  CustomerPortalSessionActivateRequest,
  CustomerPortalSessionActivateResponse,
  CustomerPortalSessionData,
  CustomerPortalSessionInitRequest,
  CustomerPortalSessionInitResponse,
  GenerateCustomerPortalShareLinkRequest,
  GenerateCustomerPortalShareLinkResponse,
  PaymentGatewayConfigurations,
  PreviewCustomerPortalSessionRequest,
  PreviewCustomerPortalSessionResponse,
  SubscriptionView,
} from '@monetize/types/app';
import {
  ApiListResponse,
  BillingPlatformApiErrorResponse,
} from '@monetize/types/common';
import {
  DefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError, AxiosHeaders, isAxiosError } from 'axios';
import { applyRulesToCustomerPortalData } from '../routes/anonymousShare/customerPortal/customerPortalUtils';
import { apiGet, apiPost, apiPut, MAxiosRequestConfig } from './axios';
import { customerPortalServiceQueryKeys } from './queryKeysService';

export const CUSTOMER_PORTAL_PREVIEW_ACCESS_TOKEN = '_PREVIEW_SESSION_';

/**
 * Apply Axios configuration to request
 * Automatically handles authenticated vs unauthenticated headers
 */
function getApiConfig({
  tenantId,
  accessToken,
  config,
}: {
  tenantId: string;
  accessToken?: string;
  config?: Partial<MAxiosRequestConfig>;
}): MAxiosRequestConfig {
  // Authenticated user - don't override header configuration
  if (accessToken === CUSTOMER_PORTAL_PREVIEW_ACCESS_TOKEN) {
    return { ...config };
  }
  const headers = config?.headers || new AxiosHeaders();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return {
    headers,
    axiosConfig: {
      excludeTokenIdFromHeader: true,
      excludeUserIdFromHeader: true,
      excludeTenantIdFromHeader: true,
      customXTenantId: tenantId,
    },
    ...config,
  } as const;
}

/**
 ***************************************************************
 * AUTHENTICATED ROUTES
 ***************************************************************
 */

export function useGetCustomerPortalAccountConfig(
  billGroupId: string,
  options: Partial<
    UseQueryOptions<CustomerPortalAccountConfigurationResponse>
  > = {},
) {
  return useQuery({
    queryKey: [...customerPortalServiceQueryKeys.accountConfig()],
    queryFn: async () =>
      apiGet<CustomerPortalAccountConfigurationResponse>(
        `/api/billingPortals/billGroups/${billGroupId}/configurations`,
      )
        .then((res) => res.data)
        .then((response) => ({
          ...response,
          emailAllowlist: (response.emailAllowlist || []).sort(),
          domainAllowlist: (response.domainAllowlist || []).sort(),
        }))
        .catch((error) => {
          // When not configured, a 404 is returned, but we want reactQuery to have a blank configuration in that case
          if (!isAxiosError(error) || error.response?.status !== 404) {
            throw error;
          }
          return {
            billGroupId,
            emailAllowlist: [],
            domainAllowlist: [],
          } satisfies CustomerPortalAccountConfigurationResponse;
        }),
    ...options,
  });
}

export function useUpdateCustomerPortalAccountConfig(
  options: Partial<
    UseMutationOptions<
      CustomerPortalAccountConfigurationResponse,
      AxiosError<
        BillingPlatformApiErrorResponse<{
          errors?: CustomerPortalConfigurationErrorContext[];
        }>
      >,
      {
        billGroupId: string;
        payload: CustomerPortalAccountConfigurationRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billGroupId, payload }) =>
      apiPost<CustomerPortalAccountConfigurationResponse>(
        `/api/billingPortals/billGroups/${billGroupId}/configurations`,
        payload,
      )
        .then((res) => res.data)
        .then((response) => ({
          ...response,
          emailAllowlist: (response.emailAllowlist || []).sort(),
          domainAllowlist: (response.domainAllowlist || []).sort(),
        })),
    onSuccess: (...params) => {
      const [response] = params;
      const queryKey = [...customerPortalServiceQueryKeys.accountConfig()];
      queryClient.setQueryData(queryKey, response);
      options?.onSuccess?.(...params);
    },
    ...options,
  });
}

/**
 * Allows an authenticated user to view the billing portal
 */
export function useGetAuthenticatedUserSession(
  options: Partial<
    UseMutationOptions<
      PreviewCustomerPortalSessionResponse,
      unknown,
      PreviewCustomerPortalSessionRequest
    >
  > = {},
) {
  return useMutation({
    mutationFn: (payload) =>
      apiPost<PreviewCustomerPortalSessionResponse>(
        `/api/billingPortals/sessions/preview`,
        payload,
      ).then((res) => res.data),
    ...options,
  });
}

/**
 * Allows an authenticated user in a tenant to generate a customer portal share link.
 */
export function useGenerateCustomerPortalShareLink(
  options: Partial<
    UseMutationOptions<
      GenerateCustomerPortalShareLinkResponse,
      unknown,
      GenerateCustomerPortalShareLinkRequest
    >
  > = {},
) {
  return useMutation({
    mutationFn: (payload) =>
      apiPost<GenerateCustomerPortalShareLinkResponse>(
        `/api/billingPortals/sessions/generateShareLink`,
        payload,
      ).then((res) => res.data),
    ...options,
  });
}

/**
 ***************************************************************
 * ANONYMOUS PUBLIC ROUTES
 ***************************************************************
 */

export function useCustomerPortalSessionInit(
  options: Partial<
    UseMutationOptions<
      CustomerPortalSessionInitResponse,
      unknown,
      CustomerPortalSessionInitRequest
    >
  > = {},
) {
  return useMutation({
    mutationFn: (payload) =>
      apiPost<CustomerPortalSessionInitResponse>(
        '/api/billingPortals/sessions/init',
        payload,
        getApiConfig({ tenantId: payload.tenantId }),
      ).then((res) => res.data),
    ...options,
  });
}

export function useCustomerPortalSessionActivate(
  options: Partial<
    UseMutationOptions<
      CustomerPortalSessionActivateResponse,
      unknown,
      CustomerPortalSessionActivateRequest
    >
  > = {},
) {
  return useMutation({
    mutationFn: (payload) =>
      apiPost<CustomerPortalSessionActivateResponse>(
        '/api/billingPortals/sessions/activate',
        payload,
        getApiConfig({
          tenantId: payload.tenantId,
          accessToken: payload.billingPortalSessionId,
        }),
      ).then((res) => res.data),
    ...options,
  });
}

export function useGetCustomerPortalBillGroupData(
  sessionData: CustomerPortalSessionData,
  options: Omit<
    Partial<
      UseQueryOptions<CustomerPortalRawData, unknown, CustomerPortalData>
    >,
    'select'
  > = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;
  return useQuery({
    queryKey: [...customerPortalServiceQueryKeys.data(sessionData)],
    queryFn: () =>
      apiGet<CustomerPortalRawData>(
        `/api/billingPortals/${sessionId}/accounts/${accountId}/billGroups/${billGroupId}`,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId, sessionId } },
        }),
      ).then((res) => res.data),
    ...options,
    select: applyRulesToCustomerPortalData,
  });
}

export function useGetCustomerPortalCompanyInfo(
  sessionData: CustomerPortalSessionData,
  options: Partial<UseQueryOptions<CustomerPortalCompanyInfoResponse>> = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;
  return useQuery({
    queryKey: [...customerPortalServiceQueryKeys.companyInfo(sessionData)],
    queryFn: () =>
      apiGet<CustomerPortalCompanyInfoResponse>(
        `/api/billingPortals/${sessionId}/companyInfo`,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId, sessionId } },
        }),
      ).then((res) => res.data),
    ...options,
  });
}

export function useGetCustomerPortalPaymentConfiguration(
  sessionData: CustomerPortalSessionData,
  options: Partial<UseQueryOptions<PaymentGatewayConfigurations>> = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;
  return useQuery({
    queryKey: [
      ...customerPortalServiceQueryKeys.paymentConfiguration(sessionData),
    ],
    queryFn: () =>
      apiGet<{ paymentGatewayConfiguration: PaymentGatewayConfigurations }>(
        `/api/billingPortals/${sessionId}/accounts/${accountId}/billGroups/${billGroupId}/paymentConfiguration`,
        getApiConfig({ tenantId, accessToken }),
      ).then((res) => res.data.paymentGatewayConfiguration),
    ...options,
  });
}

export function useGetCustomerPortalInvoicesInfiniteLoad(
  sessionData: CustomerPortalSessionData,
  options: Partial<
    DefinedInitialDataInfiniteOptions<
      ApiListResponse<CustomerPortalInvoice>,
      unknown,
      CustomerPortalInvoice[],
      any[],
      number
    >
  > = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;

  return useInfiniteQuery({
    queryKey: [...customerPortalServiceQueryKeys.invoices(sessionData)],
    queryFn: ({ pageParam }) =>
      apiGet<ApiListResponse<CustomerPortalInvoice>>(
        `/api/billingPortals/${sessionId}/invoices`,
        getApiConfig({
          tenantId,
          accessToken,
          config: {
            params: {
              accountId,
              billGroupId,
              currentPage: pageParam,
              pageSize: 10,
              sort: 'dueDate:desc,createDate:desc',
            },
          },
        }),
      ).then((res) => res.data),
    select: (data) => data.pages.flatMap((page) => page.content),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam: number) => {
      if (lastPage.totalPages > lastPageParam + 1) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    ...options,
  });
}

export function useGetCustomerPortalPaymentMethodsInfiniteLoad(
  sessionData: CustomerPortalSessionData,
  options: Partial<
    DefinedInitialDataInfiniteOptions<
      ApiListResponse<CustomerPortalPaymentMethod>,
      unknown,
      CustomerPortalPaymentMethod[],
      any[],
      number
    >
  > = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;

  return useInfiniteQuery({
    queryKey: [...customerPortalServiceQueryKeys.paymentMethods(sessionData)],
    queryFn: ({ pageParam }) =>
      apiGet<ApiListResponse<CustomerPortalPaymentMethod>>(
        `/api/billingPortals/${sessionId}/paymentMethods`,
        getApiConfig({
          tenantId,
          accessToken,
          config: {
            params: {
              accountId,
              billGroupId,
              currentPage: pageParam,
              pageSize: 25,
              sort: 'createDate:desc',
            },
          },
        }),
      ).then((res) => res.data),
    select: (data) => data.pages.flatMap((page) => page.content),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam: number) => {
      if (lastPage.totalPages > lastPageParam + 1) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    ...options,
  });
}

export function useGetCustomerPortalSubscriptionView(
  sessionData: CustomerPortalSessionData,
  options: Partial<UseQueryOptions<SubscriptionView>> = {},
) {
  const { tenantId, accountId, billGroupId, sessionId, accessToken } =
    sessionData;

  return useQuery({
    queryKey: [...customerPortalServiceQueryKeys.subscriptionView(sessionData)],
    queryFn: () =>
      apiGet<SubscriptionView>(
        `/api/billingPortals/${sessionId}/subscriptionView`,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId } },
        }),
      ).then((res) => res.data),
    ...options,
  });
}

function fetchCustomerPortalInvoicePdf({
  sessionData,
  invoiceId,
}: {
  sessionData: CustomerPortalSessionData;
  invoiceId: string;
}) {
  const { accessToken, accountId, billGroupId, sessionId, tenantId } =
    sessionData;
  return apiGet<ArrayBuffer>(
    `/api/billingPortals/${sessionId}/invoices/${invoiceId}/print`,
    getApiConfig({
      tenantId,
      accessToken,
      config: {
        params: { accountId, billGroupId, sessionId },
        headers: new AxiosHeaders({
          accept: 'application/pdf',
        }),
        responseType: 'arraybuffer',
      },
    }),
  ).then((res) => res.data);
}

/**
 * Mutation form of the invoice PDF fetch — used for one-off downloads where we
 * don't want React Query to cache the response.
 */
export function useCustomerPortalPrintInvoicePdf(
  options: Partial<
    UseMutationOptions<
      ArrayBuffer,
      unknown,
      { sessionData: CustomerPortalSessionData; invoiceId: string }
    >
  > = {},
) {
  return useMutation({
    mutationFn: fetchCustomerPortalInvoicePdf,
    ...options,
  });
}

/**
 * Query form of the invoice PDF fetch — used to render an inline PDF preview.
 * Cached per invoice so re-opening the modal does not re-fetch.
 */
export function useGetCustomerPortalInvoicePdf(
  sessionData: CustomerPortalSessionData,
  invoiceId: string | null,
  options: Partial<UseQueryOptions<ArrayBuffer>> = {},
) {
  return useQuery({
    queryKey: invoiceId
      ? [...customerPortalServiceQueryKeys.invoicePdf(sessionData, invoiceId)]
      : [...customerPortalServiceQueryKeys.invoicePdf(sessionData, 'none')],
    queryFn: () =>
      fetchCustomerPortalInvoicePdf({ sessionData, invoiceId: invoiceId! }),
    enabled: !!invoiceId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

function fetchCustomerPortalQuotePdf({
  sessionData,
  quoteId,
}: {
  sessionData: CustomerPortalSessionData;
  quoteId: string;
}): Promise<ArrayBuffer> {
  const { accessToken, accountId, billGroupId, sessionId, tenantId } =
    sessionData;
  return apiGet<ArrayBuffer>(
    `/api/billingPortals/${sessionId}/quotes/${quoteId}/print`,
    getApiConfig({
      tenantId,
      accessToken,
      config: {
        params: { accountId, billGroupId, sessionId },
        headers: new AxiosHeaders({
          accept: 'application/pdf',
        }),
        responseType: 'arraybuffer',
      },
    }),
  ).then((res) => res.data);
}

/**
 * Query form of the quote PDF fetch — used to render an inline PDF preview.
 * Cached per quote so re-opening the modal does not re-fetch.
 */
export function useGetCustomerPortalQuotePdf(
  sessionData: CustomerPortalSessionData,
  quoteId: string | null,
  options: Partial<UseQueryOptions<ArrayBuffer>> = {},
) {
  return useQuery({
    queryKey: quoteId
      ? [...customerPortalServiceQueryKeys.quotePdf(sessionData, quoteId)]
      : [...customerPortalServiceQueryKeys.quotePdf(sessionData, 'none')],
    queryFn: () =>
      fetchCustomerPortalQuotePdf({ sessionData, quoteId: quoteId! }),
    enabled: !!quoteId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useUpdateCustomerPortalContact(
  options: Partial<
    UseMutationOptions<
      CustomerPortalContactUpdateResponse,
      unknown,
      {
        sessionData: CustomerPortalSessionData;
        payload: CustomerPortalContactUpdateRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, sessionData }) => {
      const { accessToken, accountId, billGroupId, sessionId, tenantId } =
        sessionData;
      return apiPut<CustomerPortalContactUpdateResponse>(
        `/api/billingPortals/${sessionId}/contacts`,
        payload,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId, sessionId } },
        }),
      ).then((res) => res.data);
    },
    onSuccess: (...params) => {
      const [response, variables] = params;

      const queryKey = [
        ...customerPortalServiceQueryKeys.data(variables.sessionData),
      ];
      const queryData =
        queryClient.getQueryData<CustomerPortalRawData>(queryKey);

      if (queryData) {
        const updatedData: CustomerPortalRawData = {
          ...queryData,
          billGroup: {
            ...queryData.billGroup,
            billingAddress: response.billingAddress,
            billingContact: response.billingContact,
            shippingAddress: response.shippingAddress,
            shippingContact: response.shippingContact,
          },
        };
        queryClient.setQueryData(queryKey, updatedData);
      }

      queryClient.invalidateQueries({ queryKey });

      options?.onSuccess?.(...params);
    },
    ...options,
  });
}

export function useGenerateCustomerPortalPaymentMethodToken(
  options: Partial<
    UseMutationOptions<
      CustomerPortalPaymentMethodTokenResponse,
      unknown,
      {
        sessionData: CustomerPortalSessionData;
        payload: CustomerPortalPaymentMethodTokenRequest;
      }
    >
  > = {},
) {
  return useMutation({
    mutationFn: ({ sessionData, payload }) => {
      const { accessToken, accountId, billGroupId, sessionId, tenantId } =
        sessionData;
      return apiPost<CustomerPortalPaymentMethodTokenResponse>(
        `/api/billingPortals/${sessionId}/paymentMethods/token`,
        payload,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId } },
        }),
      ).then((res) => res.data);
    },
    ...options,
  });
}

export function useCreateCustomerPortalPaymentMethod(
  options: Partial<
    UseMutationOptions<
      CustomerPortalPaymentMethodCreateResponse,
      unknown,
      {
        sessionData: CustomerPortalSessionData;
        payload: CustomerPortalPaymentMethodCreateRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionData, payload }) => {
      const { accessToken, accountId, billGroupId, sessionId, tenantId } =
        sessionData;
      return apiPost<CustomerPortalPaymentMethodCreateResponse>(
        `/api/billingPortals/${sessionId}/paymentMethods`,
        payload,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId } },
        }),
      ).then((res) => res.data);
    },
    onSuccess: (...params) => {
      const [, variables] = params;
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.paymentMethods(
          variables.sessionData,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.data(variables.sessionData),
      });
      options?.onSuccess?.(...params);
    },
    ...options,
  });
}

export function useCustomerPortalPayInvoice(
  options: Partial<
    UseMutationOptions<
      CustomerPortalPaymentResponse,
      unknown,
      {
        sessionData: CustomerPortalSessionData;
        payload: CustomerPortalPaymentRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionData, payload }) => {
      const { accessToken, accountId, billGroupId, sessionId, tenantId } =
        sessionData;
      return apiPost<CustomerPortalPaymentResponse>(
        `/api/billingPortals/${sessionId}/payment`,
        payload,
        getApiConfig({
          tenantId,
          accessToken,
          config: { params: { accountId, billGroupId } },
        }),
      ).then((res) => res.data);
    },
    onSuccess: (...params) => {
      const [, variables] = params;
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.invoices(
          variables.sessionData,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.payments(
          variables.sessionData,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.data(variables.sessionData),
      });
      options?.onSuccess?.(...params);
    },
    ...options,
  });
}

export function useUpdateCustomerPortalBillGroup(
  options: Partial<
    UseMutationOptions<
      CustomerPortalBillGroupUpdateResponse,
      unknown,
      {
        sessionData: CustomerPortalSessionData;
        payload: CustomerPortalBillGroupUpdateRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionData, payload }) => {
      const { accessToken, accountId, billGroupId, sessionId, tenantId } =
        sessionData;
      return apiPost<CustomerPortalBillGroupUpdateResponse>(
        `/api/billingPortals/${sessionId}/accounts/${accountId}/billGroups/${billGroupId}`,
        payload,
        getApiConfig({
          tenantId,
          accessToken,
        }),
      ).then((res) => res.data);
    },
    onSuccess: (...params) => {
      const [, variables] = params;
      queryClient.invalidateQueries({
        queryKey: customerPortalServiceQueryKeys.data(variables.sessionData),
      });
      options?.onSuccess?.(...params);
    },
    ...options,
  });
}
